import { faChevronUp, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './Style.css';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import ModalTrigger from '../../../shared/modal/ModalTrigger';

type NumberSelectQuestionProps = {
  questionid: string;
  questionTitle: string;
  labelText: string;
  modalText?: string;
  minlimit: number;
  maxlimit: number;
  width: string;
  skippable?: boolean;
  fadeIn?: string;
  controlsInfo: string;
  onOpenModal?: (html?: any) => void;
  onSetValue: (value: { id: string; value: string }) => void;
  assessmentValues: Record<string, unknown>;
  labelId?: string;
};

export function NumberSelectQuestion({
  questionid,
  questionTitle,
  labelText,
  modalText,
  minlimit: minlimitFromProps,
  maxlimit,
  width,
  skippable,
  fadeIn,
  controlsInfo,
  onOpenModal,
  onSetValue,
  assessmentValues,
  labelId,
}: NumberSelectQuestionProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState<number>(0);
  const minlimit = Number(minlimitFromProps) || 0;

  function saveValue(newValue) {
    // smoking must be 1+
    if ((questionid !== 'smoke_per_day' || newValue > 0) && newValue >= minlimit) {
      onSetValue({ id: questionid, value: newValue });
    }
  }

  function setIncreaseNumber() {
    let newValue = Number(inputValue);
    if (newValue < maxlimit) {
      newValue++;
      setInputValue(newValue);
      saveValue(newValue);
    }
  }

  function setDecreaseNumber() {
    let newValue = Number(inputValue);
    if (newValue > 0 && newValue > minlimit) {
      newValue--;
      setInputValue(newValue);
      saveValue(newValue);
    }
  }

  function numberChangeHandler(event) {
    const numericValue = Number(event.target.value);
    setInputValue(numericValue);
    saveValue(numericValue);
  }

  const handleFocusOut = (event) => {
    if (maxlimit && Number(event.target.value) > Number(maxlimit)) {
      setInputValue(maxlimit);
      saveValue(maxlimit);
    }
    if (
      (minlimit && Number(event.target.value) < Number(minlimit)) ||
      (minlimit && !event.target.value)
    ) {
      setInputValue(minlimit);
      saveValue(minlimit);
    }
  };

  const currentFieldValue = assessmentValues[questionid];

  useEffect(() => {
    const hasBeenSet = currentFieldValue !== undefined;

    if (!hasBeenSet && inputValue === 0) {
      let defaultValue = 0;

      if (questionid === 'hours_worked') defaultValue = 35;
      else if (questionid === 'age') defaultValue = 16;
      else if (questionid === 'days_absence') defaultValue = 0;
      else if (minlimit) defaultValue = minlimit;

      setInputValue(defaultValue);
      saveValue(defaultValue);
    }

    if (hasBeenSet && inputValue === 0) {
      const newValue = Number(currentFieldValue);
      setInputValue(newValue);
    }
  }, [currentFieldValue, questionid, minlimit, inputValue]);

  const [blockOpacity, setBlockOpacity] = useState(0);

  useEffect(() => {
    setTimeout(function () {
      setBlockOpacity(1);
    }, 100);
  }, []);

  return (
    <fieldset
      id={questionid}
      className={`question_block${width}${skippable ? '' : ' req'}`}
      style={{ opacity: fadeIn ? blockOpacity : '1' }}
      data-input-type="number-select-question"
      aria-labelledby={questionTitle ? undefined : `${labelId} ${questionid}-field-label`}
    >
      {questionTitle && (
        <legend aria-labelledby={labelId} className="assessment_question_title">
          {questionTitle}
        </legend>
      )}
      {modalText && (
        <ModalTrigger
          modalText={modalText}
          onOpenModal={onOpenModal}
          question={questionid}
          questionTitle={questionTitle}
        />
      )}

      <div className="answer_block">
        <div className="number_field">
          <label
            className="titleSuffix"
            id={`${questionid}-field-label`}
            htmlFor={`numberSelect_${questionid}`}
          >
            {labelText}
          </label>
          <div className="number_container_wrapper">
            <div className="number_container fs-exclude">
              <input
                type="number"
                className="number"
                id={`numberSelect_${questionid}`}
                onChange={numberChangeHandler}
                onBlur={handleFocusOut}
                value={inputValue}
                max={maxlimit}
                min={minlimit || 0}
              />
            </div>
            <div className="number_controls">
              <button
                type="button"
                className="up_btn"
                onClick={setIncreaseNumber}
                aria-labelledby={`${questionid}_up`}
              >
                <span className="visuallyHidden" id={`${questionid}_up`}>
                  {t('A134')} {controlsInfo}
                </span>
                <FontAwesomeIcon icon={faChevronUp} className="icon" />
              </button>
              <button
                type="button"
                className="down_btn"
                onClick={setDecreaseNumber}
                aria-labelledby={`${questionid}_down`}
              >
                <span className="visuallyHidden" id={`${questionid}_down`}>
                  {t('A135')} {controlsInfo}
                </span>
                <FontAwesomeIcon icon={faChevronDown} className="icon" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </fieldset>
  );
}
